import { Box, Button, Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { X } from 'lucide-react';
import type { FC } from 'react';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import { useHostContext } from '@/context/HostContext.tsx';
import AccordionInfo from '@/pages/AgentSuccess/components/WaitingVideoModal/components/AccordionInfo';
import { partnerMetadata } from '@/services/PartnerMetadata';
import { cn } from '@/utils';
import { CREATOR_VIDEO_MAP_KEYS } from '@/utils/creatorVideoKeys';

type TWaitingVideoModal = {
	closeModal: () => void;
	isOpenedModal: boolean;
};

const WaitingVideoModal: FC<TWaitingVideoModal> = ({
	closeModal,
	isOpenedModal
}) => {
	const isMobile = useMediaQuery('(max-width: 631px)');
	const modalVideo = partnerMetadata.getCreatorVideoSource(
		CREATOR_VIDEO_MAP_KEYS.SUCCESS_MODAL
	);
	const { isMiniCreatorMode, localization } = useHostContext();

	return (
		<Modal
			centered={!isMobile && !isMiniCreatorMode}
			className={cn(
				'w-[auto] !hidden !rounded-[24px] bg-white shadow-[0_24px_48px_0_rgba(18,18,23,0.03)]',
				{ '!block': isOpenedModal }
			)}
			classNames={{
				header: cn('!pt-[32px] !px-[32px] w-full', {
					'!py-0 !px-2': isMiniCreatorMode
				}),
				title: 'w-full',
				body: cn('!px-[32px] !pb-[32px]', {
					'!px-4 !pb-2 flex-1 overflow-auto': isMiniCreatorMode
				}),
				overlay: cn({ 'absolute inset-0 bg-black/35': isMiniCreatorMode }),
				inner: cn({ 'absolute inset-0 p-0 h-full': isMiniCreatorMode }),
				content: cn({
					'h-full max-h-full w-full overflow-hidden flex flex-col':
						isMiniCreatorMode
				})
			}}
			data-testid="creator-success-introduction"
			fullScreen={isMobile}
			lockScroll={!isMiniCreatorMode}
			onClose={closeModal}
			opened={isOpenedModal}
			radius={isMobile || isMiniCreatorMode ? 0 : '24'}
			size={isMobile || isMiniCreatorMode ? '100%' : 'auto'}
			styles={{
				root: { '--mb-z-index': 1005 },
				inner: { '--mb-z-index': 1005 },
				overlay: { '--mb-z-index': 1005 }
			}}
			title={
				<div className="flex items-center gap-3 w-full justify-between">
					<span
						className={cn(
							'intro-title w-full text-2xl',
							'!text-center ![font-family:var(--font-headlines)] !font-semibold !tracking-[-0.3px]',
							{
								'sm:!text-[32px]': !isMiniCreatorMode,
								'!text-base': isMiniCreatorMode
							}
						)}
					>
						{localization.rich('creator', 'agent_success.modal.title', {
							br: () => <br />
						})}
					</span>
					<Button
						onClick={closeModal}
						p={0}
						variant="transparent"
					>
						<X className="!w-[20px] !h-[20px]" />
					</Button>
				</div>
			}
			withCloseButton={false}
			withinPortal={!isMiniCreatorMode}
		>
			<div
				className={cn('flex w-full flex-col items-center mt-[60px] h-full', {
					'sm:w-[521px]': !isMiniCreatorMode,
					'm-0': isMiniCreatorMode
				})}
			>
				<Box className="w-full flex flex-col gap-3 flex-1 min-h-0">
					{modalVideo ? (
						<Box className="max-h-[518px] overflow-hidden w-full rounded-[8px] bg-transparent">
							<VideoPlayer source={modalVideo} />
						</Box>
					) : null}

					<AccordionInfo />
				</Box>

				<Button
					className="!h-10 !text-sm !font-medium sm:max-w-fit sm:!w-[116px]"
					data-testid="creator-success-introduction-confirm"
					onClick={closeModal}
					style={{
						borderRadius: `${partnerMetadata.getSubmitCtaRadius()}px`
					}}
					type="submit"
				>
					{localization.translate('common', 'ok')}
				</Button>
			</div>
		</Modal>
	);
};

export default WaitingVideoModal;
